import { cilLocationPin } from "@coreui/icons";
import CIcon from "@coreui/icons-react";
import {
  CButton,
  CModal,
  CModalBody,
  CModalHeader,
  CModalTitle,
  CTable,
  CTableBody,
  CTableDataCell,
  CTableHead,
  CTableHeaderCell,
  CTableRow,
} from "@coreui/react";
import moment from "moment";
import { useEffect, useState } from "react";
import GpsLocationModal from "src/components/GPSLocation/GpsLocationModal";
import { getDeviceHistoryById } from "src/services/gps-location.services";
import { removeArabic } from "src/uitils/commonFunction";

export default function DeviceHistoryDetailsModal({
  deviceId,
  close,
  ts1,
  ts2,
}) {
  const [isLoading, setIsLoading] = useState(true);
  const [historyData, setHistoryData] = useState([]);
  const [places, setPlaces] = useState({});
  const [batchSize, setBatchSize] = useState(5);

  const [selectedDevice, setSelectedDevice] = useState(null);
  const controller = new AbortController();

  const fetchHistoryData = async () => {
    try {
      setIsLoading(true);
      const response = await getDeviceHistoryById(
        deviceId,
        ts1.valueOf(),
        ts2.valueOf()
      );
      setHistoryData(response);
      setIsLoading(false);
    } catch (error) {
      console.log(error);
      setIsLoading(false);
    }
  };

  const fetchPlaceName = async (lat, lon, signal) => {
    let token =
      "pk.eyJ1Ijoid3NpdC1zYW1pdWwiLCJhIjoiY2xqM3g1b3FoMWtpODNyb2RmM2V1aTJzbCJ9.u870I5zQLy6w3dWBby5siw";
    const url = `https://api.mapbox.com/geocoding/v5/mapbox.places/${lon},${lat}.json?access_token=${token}&language=en`;
    try {
      const response = await fetch(url, { signal });
      const data = await response.json();
      return data.features?.length > 0
        ? data.features[0].place_name
        : "No place found";
    } catch (error){}
  };

  const getLatLongIndex = (lat, lon) => {
    if (lat == null || lon == null) {
      return Math.random();
    }
    return lat?.toString() + "," + lon?.toString();
  };

  const processLocationsInBatches = async (locations) => {
    let index = 0;

    while (index < locations.length) {
      // Check if aborted *after* the batch fetches complete but *before* setting state
      if (controller.signal.aborted) {
        console.log("Batch processing aborted before setting state.");
        return;
      }

      const batch = locations.slice(index, index + batchSize);
      const results = await Promise.all(
        batch.map(({ lat, lon }) => fetchPlaceName(lat, lon, controller.signal))
      );

      setPlaces((prevPlaces) => {
        const newPlaces = { ...prevPlaces };
        batch.forEach((loc, i) => {
          newPlaces[getLatLongIndex(loc.lat, loc.lon)] = results[i];
        });
        return newPlaces;
      });

      index += batchSize;
      await new Promise((resolve) => setTimeout(resolve, 500)); // Prevent API throttling
    }
  };

  const getLatsLongs = (isOld) => {
    let latsLongs = [];
    historyData?.locationData?.forEach((item) => {
      if (!places[getLatLongIndex(item?.latitude, item?.longitude)]) {
        latsLongs.push({
          lat: item?.latitude,
          lon: item?.longitude,
        });
      }
    });
    console.log(latsLongs);
    return latsLongs;
  };

  useEffect(() => {
    if (historyData?.locationData?.length <= 0) {
      return;
    }
    if (Object.keys(places).length > 0) {
      processLocationsInBatches(getLatsLongs(true));
      return;
    } else {
      processLocationsInBatches(getLatsLongs());
    }
    // Cleanup function for this effect
    return () => {
      console.log("Aborting place name fetching due to effect cleanup (historyData change or unmount).");
      controller.abort(); // Abort the ongoing fetches
    };
  }, [historyData]);

  useEffect(() => {
    fetchHistoryData();
  }, [deviceId]);

  return (
    <>
      <CModal size="xl" visible={!!deviceId} onClose={() => close(null)}>
        <CModalHeader closeButton>
          <CModalTitle>Vehicle History</CModalTitle>
        </CModalHeader>
        <CModalBody>
          <CTable align="middle" className="mb-0 border" hover responsive>
            <CTableHead color="light">
              <CTableRow>
                <CTableHeaderCell className="text-center">
                  Time
                </CTableHeaderCell>
                <CTableHeaderCell className="text-center">
                  Driver
                </CTableHeaderCell>
                <CTableHeaderCell className="text-center">
                  Status
                </CTableHeaderCell>
                <CTableHeaderCell className="text-center">Map</CTableHeaderCell>
                <CTableHeaderCell className="text-center">
                  Location
                </CTableHeaderCell>
              </CTableRow>
            </CTableHead>
            <CTableBody>
              {isLoading && (
                <CTableRow className="text-center">
                  <CTableDataCell colSpan="5">Loading...</CTableDataCell>
                </CTableRow>
              )}
              {!isLoading &&
                historyData?.locationData?.map((item, index) => (
                  <CTableRow className="text-center" key={index + 1}>
                    <CTableDataCell>
                      {item?.timestamp &&
                        moment(item?.timestamp)?.format("DD-MMM-YYYY hh:mm a")}
                    </CTableDataCell>
                    <CTableDataCell>
                      {item?.driverName?.replace(",","").trim()}
                    </CTableDataCell>
                    <CTableDataCell>
                      {item?.speed > 0 && (
                        <CButton
                          size="sm"
                          color="success"
                          className="text-white"
                          style={{ width: "80px" }}
                        >
                          Moving
                        </CButton>
                      )}

                      {item?.speed == 0 && (
                        <CButton
                          size="sm"
                          color="danger"
                          className="text-white"
                          style={{ width: "80px" }}
                        >
                          Stopped
                        </CButton>
                      )}
                    </CTableDataCell>
                    <CTableDataCell className="text-center">
                      <CButton
                        size="sm"
                        onClick={() =>
                          setSelectedDevice({
                            asset: { name: historyData?.user?.vehicleId },
                            deviceStatus: {
                              currentPosition: {
                                latitude: item?.latitude,
                                longitude: item?.longitude,
                              },
                            },
                          })
                        }
                        disabled={!item?.latitude || !item?.longitude}
                      >
                        <CIcon icon={cilLocationPin} />
                      </CButton>
                    </CTableDataCell>
                    <CTableDataCell className="text-center fw-bold">
                      {removeArabic(
                        places[getLatLongIndex(item?.latitude, item?.longitude)]
                      )}
                    </CTableDataCell>
                  </CTableRow>
                ))}

              {!isLoading && historyData?.locationData?.length <= 0 && (
                <CTableRow className="text-center">
                  <CTableDataCell colSpan="5">
                    There is no data!!
                  </CTableDataCell>
                </CTableRow>
              )}
            </CTableBody>
          </CTable>
        </CModalBody>
      </CModal>

      {selectedDevice && (
        <GpsLocationModal {...{ selectedDevice, setSelectedDevice }} />
      )}
    </>
  );
}
